import { FC, useEffect } from 'react';
import Page, { PageProps } from '@/components/page';
import RouterTabs from '@/components/router-tabs';
import { useQuery } from 'react-query';
import { getPolicy } from '@/lib/services/policy.service';
import useApp from '@/hooks/use-app.hook';
import { PolicyType } from '@/lib/models/policy-model';
import { getPathSegments } from '@/lib/url-helpers';
import GroupPolicySensitiveDataForm from './sensitive-data-form';
import PortalForm from './portal-form';
import McpAccessForm from './mcp-access-form';
import ScopeForm from './scope-form';
import MetadataForm from './metadata-form';
import { QueryKey } from '@/lib/query-client';
import PolicyPageMenu from '@/pages/policy/menu';
import ServiceAccessForm from './service-access-form';
import useFeature from '@/hooks/use-feature.hook';
import { Navigate } from '@tanstack/react-router';

const PolicyViewPage: FC<PageProps> = () => {
  // absolutely could not get useParams to work
  const [, , paramPolicyType, paramPolicyId] = getPathSegments();

  const { setPageTitle, setPageMenu } = useApp();
  const { hasPolicyV2 } = useFeature();

  const query = useQuery([QueryKey.PolicyView, paramPolicyType, paramPolicyId], async () =>
    getPolicy(paramPolicyType as PolicyType, paramPolicyId)
  );
  const { data: policy, isLoading } = query;

  useEffect(() => {
    if (!policy) {
      return;
    }

    const isScoped = paramPolicyType === 'scoped';
    const { niceName } = policy;

    if (isScoped) {
      setPageTitle(niceName, 'scoped-policy');
      setPageMenu(<PolicyPageMenu policy={policy} />);
    } else {
      setPageTitle(niceName);
    }
  }, [policy, setPageTitle, setPageMenu, paramPolicyType]);

  if (!hasPolicyV2) {
    return <Navigate to="/config/group-policies" />;
  }

  if (!isLoading && !policy) {
    return <Navigate to="/config/policy/scoped" />;
  }

  const policyId = policy?.id || '';

  return (
    <Page query={query}>
      <RouterTabs
        panels={[
          {
            label: 'Scope',
            path: 'scope',
            component: <ScopeForm policyId={policyId} />,
          },
          {
            label: 'Routing',
            path: 'service-access',
            component: <ServiceAccessForm policyId={policyId} />,
          },
          {
            label: 'MCP Tools',
            path: 'mcp-tools',
            component: <McpAccessForm policyId={policyId} />,
          },
          {
            label: 'Sensitive Data',
            path: 'sensitive-data',
            component: <GroupPolicySensitiveDataForm policyId={policyId} />,
          },
          {
            label: 'Classifiers',
            path: 'classifiers',
            component: <MetadataForm policyId={policyId} />,
          },
          { label: 'Portal', path: 'portal', component: <PortalForm policyId={policyId} /> },
        ]}
      />
    </Page>
  );
};

export default PolicyViewPage;
